import { useStore } from '../store/useStore';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Landmark,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  Scale,
  CalendarRange,
  Zap,
  Users,
  FileText,
  Receipt,
  IndianRupee,
} from 'lucide-react';
import { motion } from 'motion/react';

import { useSystemDate } from '../hooks/useSystemDate';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { formatDate } from '../lib/dateUtils';
import { useBusinessNature } from '../hooks/useBusinessNature';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { AlertTriangle } from 'lucide-react';

export const DashboardView = () => {
  const activeCompany = useStore((state) => state.activeCompany);
  const { has: hasFeature } = useBusinessNature();
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);
  const systemDate = useSystemDate();

  const stats = useDashboardStats();

  if (!stats) return <div className="p-8 text-gray-500">Loading overview...</div>;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { y: 16, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  const workingCapital = stats.cashBankBalance + stats.receivables - stats.payables;

  const kpiCards = [
    { label: 'Total Sales', value: stats.totalSales, icon: TrendingUp, iconBg: 'bg-[#f0f9f9]', iconColor: 'text-[#1e8585]' },
    { label: 'Total Purchase', value: stats.totalPurchase, icon: TrendingDown, iconBg: 'bg-orange-50', iconColor: 'text-orange-600' },
    { label: 'Cash Balance', value: stats.cashBalance, icon: Wallet, iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { label: 'Bank Balance', value: stats.bankBalance, icon: Landmark, iconBg: 'bg-blue-50', iconColor: 'text-blue-600' },
    { label: 'Receivables', value: stats.receivables, icon: ArrowUpRight, iconBg: 'bg-cyan-50', iconColor: 'text-cyan-600' },
    { label: 'Payables', value: stats.payables, icon: ArrowDownRight, iconBg: 'bg-rose-50', iconColor: 'text-rose-600' },
  ];

  return (
    <div className="flex flex-col w-full h-full bg-[#f8fafc] overflow-y-auto p-6 font-sans">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Business Overview</h1>
          <p className="text-slate-500 text-sm">Real-time insights for {activeCompany?.name}</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 shadow-sm text-xs text-slate-600">
          <CalendarRange className="w-4 h-4 text-[#1e8585]" />
          <span>{formatDate(fromDate)} – {formatDate(toDate)}</span>
        </div>
      </div>

      {/* KPI Cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6"
      >
        {kpiCards.map((card) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              variants={itemVariants}
              className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              <div className={`inline-flex p-2 rounded-lg mb-3 ${card.iconBg}`}>
                <Icon className={`w-5 h-5 ${card.iconColor}`} />
              </div>
              <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide mb-1">{card.label}</div>
              <div className="text-xl font-bold text-slate-800 truncate" title={formatCurrency(card.value)}>
                {formatCurrency(card.value)}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Net Profit / Loss Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center justify-between rounded-xl border px-6 py-4 mb-6 shadow-sm ${
          stats.profitLoss >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${stats.profitLoss >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
            <Scale className={`w-5 h-5 ${stats.profitLoss >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {stats.profitLoss >= 0 ? 'Net Profit' : 'Net Loss'} (Current Period)
            </div>
            <div className="text-xs text-slate-400">Income − Expenses (incl. Sales &amp; Purchase)</div>
          </div>
        </div>
        <div className={`text-2xl font-bold ${stats.profitLoss >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
          {formatCurrency(stats.profitLoss)}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Charts Section */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-slate-800 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-[#1e8585]" /> Sales &amp; Purchase Trend
              </h3>
            </div>
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e8585" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#1e8585" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorPurchase" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} tickFormatter={(val) => `₹${val/1000}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    formatter={(value: number, name: string) => [formatCurrency(value), name.charAt(0).toUpperCase() + name.slice(1)]}
                  />
                  <Area type="monotone" dataKey="sales" stroke="#1e8585" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                  <Area type="monotone" dataKey="purchase" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorPurchase)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        {/* Right Column — Today + Top Parties */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex flex-col gap-6 h-full"
        >
          {/* Today's Snapshot */}
          <div className="bg-gradient-to-br from-[#1e8585] to-[#167070] p-5 rounded-xl text-white shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-emerald-300" />
              <h3 className="font-bold text-sm">Today's Snapshot</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wide text-white/60 mb-1">Vouchers</div>
                <div className="text-xl font-bold">{stats.todayStats.voucherCount}</div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wide text-white/60 mb-1">Total Value</div>
                <div className="text-xl font-bold truncate" title={formatCurrency(stats.todayStats.totalAmount)}>
                  {formatCurrency(stats.todayStats.totalAmount)}
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/60 mb-1">
                  <Receipt className="w-3 h-3" /> Sales
                </div>
                <div className="text-sm font-bold">{stats.todayStats.salesCount} <span className="text-xs font-normal text-white/50">entries</span></div>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/60 mb-1">
                  <FileText className="w-3 h-3" /> Purchase
                </div>
                <div className="text-sm font-bold">{stats.todayStats.purchaseCount} <span className="text-xs font-normal text-white/50">entries</span></div>
              </div>
            </div>
          </div>

          {/* Top Receivables */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex-1">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-cyan-600" />
              <h3 className="font-bold text-slate-800 text-sm">Top Receivables</h3>
            </div>
            {stats.topDebtors.length === 0 ? (
              <div className="h-16 flex items-center justify-center text-xs text-slate-400">No outstanding receivables</div>
            ) : (
              <div className="space-y-3">
                {stats.topDebtors.map((d: any, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-cyan-50 text-cyan-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {d.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-700 truncate">{d.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800 flex-shrink-0 ml-2">{formatCurrency(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Payables */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex-1">
            <div className="flex items-center gap-2 mb-4">
              <IndianRupee className="w-4 h-4 text-rose-500" />
              <h3 className="font-bold text-slate-800 text-sm">Top Payables</h3>
            </div>
            {stats.topCreditors.length === 0 ? (
              <div className="h-16 flex items-center justify-center text-xs text-slate-400">No outstanding payables</div>
            ) : (
              <div className="space-y-3">
                {stats.topCreditors.map((c: any, i: number) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-500 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-700 truncate">{c.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-800 flex-shrink-0 ml-2">{formatCurrency(c.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Bottom Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center text-slate-800 font-bold mb-4">
            <Package className="w-4 h-4 mr-2 text-emerald-600" /> Inventory Summary
          </div>
          <div className="text-2xl font-bold text-slate-800 mb-1">{formatCurrency(stats.inventoryValue)}</div>
          <div className="text-xs text-slate-500">Current stock valuation</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center text-slate-800 font-bold mb-4">
            <Scale className="w-4 h-4 mr-2 text-[#1e8585]" /> Working Capital
          </div>
          <div className={`text-2xl font-bold mb-1 ${workingCapital >= 0 ? 'text-slate-800' : 'text-rose-600'}`}>
            {formatCurrency(workingCapital)}
          </div>
          <div className="text-xs text-slate-500">Cash + Bank + Receivables − Payables</div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center text-slate-800 font-bold mb-4">
            <CalendarRange className="w-4 h-4 mr-2 text-purple-600" /> Financial Year
          </div>
          <div className="text-sm font-semibold text-slate-800 mb-1">
            Started: {formatDate(activeCompany?.financialYearStart)}
          </div>
          <div className="text-xs text-slate-500 mb-2">
            Books begin from: {formatDate(activeCompany?.booksBeginFrom)}
          </div>
          <div className="text-xs text-slate-400">As on: {formatDate(systemDate)}</div>
        </div>
      </motion.div>

      {/* Pharmacy: Expiry Alert Widget */}
      {hasFeature('expiryTracking') && <ExpiryAlertWidget />}

      {/* GST Due Date Widget — always shown if company has GSTIN */}
      {(activeCompany as any)?.gstin && <GstDueDateWidget />}

      {/* Low Stock Alert Widget */}
      <LowStockWidget />
    </div>
  );
};

const ExpiryAlertWidget = () => {
  const activeCompany = useStore((state) => state.activeCompany);

  const expiryData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    const items = await db.stockItems.where('companyId').equals(activeCompany.id!).toArray();
    const invEntries = await db.inventoryEntries.toArray();
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 86400000);
    const in90 = new Date(now.getTime() + 90 * 86400000);

    const alerts: { name: string; batch: string; expiry: string; daysLeft: number; qty: number }[] = [];

    for (const entry of invEntries) {
      if (!(entry as any).expiryDate) continue;
      const item = items.find(i => i.id === entry.stockItemId);
      if (!item || item.companyId !== activeCompany.id!) continue;
      const [ey, em] = (entry as any).expiryDate.split('-').map(Number);
      if (!ey || !em) continue;
      const expiryDate = new Date(ey, em - 1, 28);
      if (expiryDate > in90) continue;
      const daysLeft = Math.ceil((expiryDate.getTime() - now.getTime()) / 86400000);
      alerts.push({
        name: item.name,
        batch: (entry as any).batchNumber || '',
        expiry: (entry as any).expiryDate,
        daysLeft,
        qty: entry.quantity,
      });
    }

    alerts.sort((a, b) => a.daysLeft - b.daysLeft);
    const expired = alerts.filter(a => a.daysLeft <= 0);
    const expiring30 = alerts.filter(a => a.daysLeft > 0 && a.daysLeft <= 30);
    const expiring90 = alerts.filter(a => a.daysLeft > 30 && a.daysLeft <= 90);
    return { expired, expiring30, expiring90, total: alerts.length };
  }, [activeCompany]);

  if (!expiryData || expiryData.total === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-slate-800 font-bold">
            <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" /> Expiry Alerts
          </div>
          <div className="flex gap-3 text-xs">
            {expiryData.expired.length > 0 && <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-bold">{expiryData.expired.length} Expired</span>}
            {expiryData.expiring30.length > 0 && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{expiryData.expiring30.length} in 30 days</span>}
            {expiryData.expiring90.length > 0 && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">{expiryData.expiring90.length} in 90 days</span>}
          </div>
        </div>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {[...expiryData.expired, ...expiryData.expiring30, ...expiryData.expiring90].slice(0, 15).map((a, i) => (
            <div key={i} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${
              a.daysLeft <= 0 ? 'bg-rose-50 text-rose-800' : a.daysLeft <= 30 ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'
            }`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="font-bold truncate">{a.name}</span>
                <span className="text-[10px] opacity-70">Batch: {a.batch || '—'}</span>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-[10px]">Qty: {a.qty}</span>
                <span className="font-bold text-[11px]">
                  {a.daysLeft <= 0 ? 'EXPIRED' : `${a.daysLeft}d left`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ── GST Due Date Widget ───────────────────────────────────────────────────────
const GstDueDateWidget = () => {
  const activeCompany = useStore(s => s.activeCompany);
  const systemDate    = useSystemDate();

  const gstData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    const now = new Date(systemDate);
    const month = now.getMonth();
    const year  = now.getFullYear();

    // GSTR-1: 11th of following month; GSTR-3B: 20th of following month
    const nextM = month === 11 ? 0 : month + 1;
    const nextY = month === 11 ? year + 1 : year;
    const gstr1Due  = new Date(nextY, nextM, 11);
    const gstr3bDue = new Date(nextY, nextM, 20);

    const diffDays = (d: Date) => Math.ceil((d.getTime() - now.getTime()) / 86400000);
    const fmtD = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

    // Current month's GST from vouchers
    const mStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => v.date.startsWith(mStr) && !v.deleted && ['Sales','Credit Note','Purchase','Debit Note'].includes(v.type))
      .toArray();
    const vIds = vouchers.map(v => v.id!);
    const entries = vIds.length ? await db.voucherEntries.where('voucherId').anyOf(vIds).toArray() : [];
    const ledgers = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const ledgerMap = new Map(ledgers.map(l => [l.id!, l]));

    let outTax = 0, inTax = 0;
    entries.forEach(e => {
      const l = ledgerMap.get(e.ledgerId);
      if (!l || !['CGST','SGST','IGST','CESS'].includes(l.name)) return;
      const v = vouchers.find(v => v.id === e.voucherId);
      if (!v) return;
      if (['Sales','Credit Note'].includes(v.type)) outTax += e.amount;
      else inTax += e.amount;
    });

    return {
      gstr1:  { due: fmtD(gstr1Due),  days: diffDays(gstr1Due)  },
      gstr3b: { due: fmtD(gstr3bDue), days: diffDays(gstr3bDue) },
      outTax, inTax, netPayable: Math.max(0, outTax - inTax),
    };
  }, [activeCompany, systemDate]);

  if (!gstData) return null;

  const urgencyColor = (days: number) =>
    days <= 3 ? 'text-red-700 bg-red-50 border-red-200' :
    days <= 7 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                'text-teal-700 bg-[#f0fafa] border-[#cde8e8]';

  return (
    <motion.div variants={{ hidden: { y: 16, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mt-0">
      <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex p-1.5 rounded-lg bg-[#f0f9f9]">
          <svg className="w-4 h-4 text-[#1e8585]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"/>
          </svg>
        </div>
        <h3 className="font-bold text-slate-700 text-sm">GST Filing Calendar</h3>
        <span className="text-[10px] text-slate-400 ml-1">This month</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          { label: 'GSTR-1 due', ...gstData.gstr1 },
          { label: 'GSTR-3B due', ...gstData.gstr3b },
        ].map(f => (
          <div key={f.label} className={`border rounded-lg px-3 py-2 ${urgencyColor(f.days)}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{f.label}</div>
            <div className="font-bold text-[13px]">{f.due}</div>
            <div className="text-[10px] font-medium">{f.days <= 0 ? '⚠ OVERDUE' : `${f.days} days left`}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[
          { label: 'Output Tax', val: gstData.outTax, color: 'text-slate-700' },
          { label: 'ITC Available', val: gstData.inTax, color: 'text-emerald-600' },
          { label: 'Net Payable', val: gstData.netPayable, color: gstData.netPayable > 0 ? 'text-rose-700' : 'text-emerald-700' },
        ].map(c => (
          <div key={c.label} className="bg-slate-50 rounded-lg p-2">
            <div className="text-[9px] text-slate-500 font-semibold uppercase tracking-wide">{c.label}</div>
            <div className={`font-bold text-[12px] font-mono ${c.color}`}>
              ₹{c.val.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ── Low Stock Alert Widget ────────────────────────────────────────────────────
const LowStockWidget = () => {
  const activeCompany = useStore(s => s.activeCompany);

  const lowStockItems = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const items = await db.stockItems.where('companyId').equals(activeCompany.id!).filter(s => !s.isDeleted).toArray();
    const hasReorder = items.filter(i => (i.reorderLevel || 0) > 0);
    if (!hasReorder.length) return [];

    const invEntries = await db.inventoryEntries
      .where('stockItemId').anyOf(hasReorder.map(i => i.id!)).toArray();
    const stockMap = new Map<number, number>();
    hasReorder.forEach(i => stockMap.set(i.id!, i.openingBalance || 0));
    invEntries.forEach(e => { stockMap.set(e.stockItemId, (stockMap.get(e.stockItemId) || 0) + (e.quantity || 0)); });

    return hasReorder
      .filter(i => (stockMap.get(i.id!) || 0) <= (i.reorderLevel || 0))
      .map(i => ({ name: i.name, stock: stockMap.get(i.id!) || 0, reorder: i.reorderLevel || 0 }))
      .sort((a, b) => (a.stock / (a.reorder || 1)) - (b.stock / (b.reorder || 1)))
      .slice(0, 5);
  }, [activeCompany]);

  if (!lowStockItems || lowStockItems.length === 0) return null;

  return (
    <motion.div variants={{ hidden: { y: 16, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mt-0">
      <div className="flex items-center gap-2 mb-3">
        <div className="inline-flex p-1.5 rounded-lg bg-rose-50">
          <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          </svg>
        </div>
        <h3 className="font-bold text-slate-700 text-sm">Low Stock Alert</h3>
        <span className="ml-auto text-[10px] text-rose-600 font-semibold bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
          {lowStockItems.length} item{lowStockItems.length > 1 ? 's' : ''} below reorder
        </span>
      </div>
      <div className="space-y-1.5">
        {lowStockItems.map((item, i) => {
          const pct = item.reorder > 0 ? Math.max(0, Math.min(1, item.stock / item.reorder)) : 0;
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-[11px] font-medium text-slate-700 w-40 truncate flex-shrink-0">{item.name}</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${pct <= 0 ? 'bg-red-500' : pct < 0.5 ? 'bg-amber-400' : 'bg-orange-400'}`}
                  style={{ width: `${Math.max(2, pct * 100)}%` }} />
              </div>
              <span className={`text-[10px] font-bold w-20 text-right flex-shrink-0 ${item.stock <= 0 ? 'text-red-600' : 'text-amber-600'}`}>
                {item.stock <= 0 ? 'OUT OF STOCK' : `${item.stock} (min ${item.reorder})`}
              </span>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};
